import axios, { AxiosRequestConfig } from 'axios';
import { baseURL } from '../config';
import ApiHelper from './ApiHelper';
import IResponse from '../interfaces/IResponse';

const BASE_URL = `${baseURL}api/non-working-days`;

export interface INonWorkingFactory {
    _id: string;
    factoryName: string;
    image?: string;
    breakStartDate?: string | null;
    breakEndDate?: string | null;
    orderDeadlineDate?: string | null;
    message?: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface INonWorkingDays {
    _id: string;
    hasUpcomingHoliday: boolean;
    holidayName: string;
    factories: INonWorkingFactory[];
    createdAt?: string;
    updatedAt?: string;
}

export interface IHolidayEventPayload {
    hasUpcomingHoliday: boolean;
    holidayName: string;
}

export interface IFactoryPayload {
    factoryName: string;
    breakStartDate?: string;
    breakEndDate?: string;
    orderDeadlineDate?: string;
    message?: string;
    image?: File | null;
}

interface INonWorkingDaysResponse extends IResponse {
    nonWorkingDays: INonWorkingDays | null;
}

interface IFactoryResponse extends IResponse {
    factory: INonWorkingFactory | null;
}

const getMessage = (error: unknown, fallback: string) => {
    if (axios.isAxiosError(error) && error.response) {
        return {
            message: error.response.data?.message || fallback,
            status: error.response.status || 400,
        };
    }

    return { message: fallback, status: 400 };
};

const toFormData = (payload: IFactoryPayload) => {
    const formData = new FormData();

    formData.append('factoryName', payload.factoryName);
    if (payload.breakStartDate !== undefined) formData.append('breakStartDate', payload.breakStartDate);
    if (payload.breakEndDate !== undefined) formData.append('breakEndDate', payload.breakEndDate);
    if (payload.orderDeadlineDate !== undefined) formData.append('orderDeadlineDate', payload.orderDeadlineDate);
    if (payload.message !== undefined) formData.append('message', payload.message);

    if (payload.image) {
        formData.append('image', payload.image);
    }

    return formData;
};

const getNonWorkingDays = async (): Promise<INonWorkingDaysResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.get(BASE_URL, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Pobrano wolne fabryk',
            nonWorkingDays: res.data?.nonWorkingDays || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie pobrano wolnego fabryk');

        return {
            ok: false,
            status,
            message,
            nonWorkingDays: null,
        };
    }
};

const updateHolidayEvent = async (payload: IHolidayEventPayload): Promise<INonWorkingDaysResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.put(`${BASE_URL}/event`, payload, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Zapisano wydarzenie',
            nonWorkingDays: res.data?.nonWorkingDays || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie zapisano wydarzenia');

        return {
            ok: false,
            status,
            message,
            nonWorkingDays: null,
        };
    }
};

const endHolidayEvent = async (): Promise<INonWorkingDaysResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.put(`${BASE_URL}/end-event`, {}, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Zakonczono wydarzenie',
            nonWorkingDays: res.data?.nonWorkingDays || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie zakonczono wydarzenia');

        return {
            ok: false,
            status,
            message,
            nonWorkingDays: null,
        };
    }
};

const getFactory = async (_id: string): Promise<IFactoryResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.get(`${BASE_URL}/factories/${_id}`, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Pobrano fabryke',
            factory: res.data?.data?.factory || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie pobrano fabryki');

        return {
            ok: false,
            status,
            message,
            factory: null,
        };
    }
};

const addFactory = async (payload: IFactoryPayload): Promise<INonWorkingDaysResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeaderForFiles(),
        };

        const res = await axios.post(`${BASE_URL}/factories`, toFormData(payload), config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Dodano fabryke',
            nonWorkingDays: res.data?.nonWorkingDays || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie dodano fabryki');

        return {
            ok: false,
            status,
            message,
            nonWorkingDays: null,
        };
    }
};

const editFactory = async (_id: string, payload: IFactoryPayload): Promise<INonWorkingDaysResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeaderForFiles(),
        };

        const res = await axios.put(`${BASE_URL}/factories/${_id}`, toFormData(payload), config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Zapisano fabryke',
            nonWorkingDays: res.data?.nonWorkingDays || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie zapisano fabryki');

        return {
            ok: false,
            status,
            message,
            nonWorkingDays: null,
        };
    }
};

const deleteFactory = async (_id: string): Promise<IResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.delete(`${BASE_URL}/factories/${_id}`, config);

        return {
            ok: true,
            status: res.status,
            message: 'Usunieto fabryke',
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie usunieto fabryki');

        return {
            ok: false,
            status,
            message,
        };
    }
};

const deleteFactoryImage = async (_id: string): Promise<INonWorkingDaysResponse> => {
    try {
        const config: AxiosRequestConfig = {
            headers: ApiHelper.createHeader(),
        };

        const res = await axios.get(`${BASE_URL}/factories/${_id}/deleteImg`, config);

        return {
            ok: true,
            status: res.status,
            message: res.data?.message || 'Usunieto zdjecie',
            nonWorkingDays: res.data?.nonWorkingDays || null,
        };
    } catch (error) {
        const { message, status } = getMessage(error, 'Nie usunieto zdjecia');

        return {
            ok: false,
            status,
            message,
            nonWorkingDays: null,
        };
    }
};

const nonWorkingDaysController = {
    getNonWorkingDays,
    updateHolidayEvent,
    endHolidayEvent,
    getFactory,
    addFactory,
    editFactory,
    deleteFactory,
    deleteFactoryImage,
};

export default nonWorkingDaysController;
